import React, { useEffect, useState, useMemo } from 'react';
import { DayTabs, SessionModalWithContext as SessionModal, SponsorsSection } from '@portal/components/event_common';
import { ExpandedSessionCard } from './ExpandedSessionCard';
import { PortalModal } from '../PortalModal';
import { SpeakerModal } from '../speakers/SpeakerModal';
import { useSpeakers } from '../../hooks/useSpeakers';
import { useSponsors } from '../../hooks/useSponsors';
import { SponsorDetailModal } from '../sponsors/SponsorDetailModal';
import { Search, Filter, X, FileText, MessageSquare, Tv, HeartHandshake, Download, FileDown, Calendar, Clock, ChevronDown, FileIcon, Image as ImageIcon, FileSpreadsheet, File } from 'lucide-react';
import { CategoryBadge, CategoryFilters } from '@portal/components/event_common';
import type { Category } from '@portal/types/agenda.types';
import type { CategoryWithCount } from '@portal/public_event/types/exhibitor.types';
import { ScheduleLoadingState } from './ScheduleLoadingState';
import { useEventContext } from '../../contexts/EventContext';
import { useAgendas, useAgenda } from '../../../hooks/useAgendas';
import { useEventDetails } from '../../../hooks/useEventDetails';
import type { Attachment, ScheduleSession } from '../../../types/schedule.types';
import { transformAgendaToScheduleData, generateCalendarUrl } from './utils/dataTransformers';
import { getDefaultScheduleDay } from '../../../utils/scheduleHelpers';
import TIMEZONES from '../../../../shared/timezones';

export const SchedulePage = () => {
  const { eventSlug, style } = useEventContext();
  const primaryColor = style?.headerColor || '#2563eb'; // Default to blue if no color provided
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedDayId, setSelectedDayId] = useState<string | undefined>();
  const [selectedSession, setSelectedSession] = useState<ScheduleSession | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showMaterialsTab, setShowMaterialsTab] = useState(false);
  // Always use list view with ExpandedSessionCard
  const viewType = 'list';
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);

  // Fetch speakers data for modal
  const { data: speakersData } = useSpeakers(eventSlug);

  // Fetch sponsors data
  const { data: sponsorsResponse, isLoading: sponsorsLoading } = useSponsors(eventSlug);
  const [selectedSponsor, setSelectedSponsor] = useState<any | null>(null);

  // Fetch event details for timezone
  const { data: eventDetails } = useEventDetails(eventSlug);

  // Update page title with custom navigation name
  useEffect(() => {
    if (eventDetails) {
      const scheduleName = eventDetails.eventOption?.scheduleName || 'Schedule';
      document.title = `${scheduleName} - ${eventDetails.name}`;
    }
  }, [eventDetails]);

  // Fetch agendas list
  const { data: agendas, isLoading: agendasLoading, error: agendasError } = useAgendas(eventSlug);

  // Get primary agenda or first one
  const primaryAgenda = agendas?.find(a => a.primary) || agendas?.[0];

  // Fetch full agenda with days and sessions
  const { data: agendaData, isLoading: agendaLoading, error: agendaError } = useAgenda(
    eventSlug,
    primaryAgenda?.id || null
  );

  // Transform agenda data to component format
  const scheduleData = agendaData ? transformAgendaToScheduleData(agendaData) : [];

  // Set default day when schedule data is loaded
  useEffect(() => {
    if (scheduleData.length > 0 && !selectedDay) {
      // Use helper to find the optimal default day
      const defaultDay = getDefaultScheduleDay(scheduleData, eventDetails?.timeZone);

      if (defaultDay) {
        setSelectedDay(defaultDay.date);
        setSelectedDayId(defaultDay.id);
      }
    }
  }, [scheduleData, selectedDay, eventDetails?.timeZone]);

  // No longer need to set viewType as it's always 'list'

  // Close download menu on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDownloadMenuOpen) {
        setIsDownloadMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDownloadMenuOpen]);

  const isLoading = agendasLoading || agendaLoading;
  const hasError = agendasError || agendaError;

  // Timezone information
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const eventTimeZone = eventDetails?.timeZone || 'America/Los_Angeles';

  // Helper function to get timezone label
  const getTimezoneLabel = (timezoneValue: string): string => {
    const timezone = TIMEZONES.find(tz => tz.value === timezoneValue);
    return timezone?.label || timezoneValue.replace('_', ' ');
  };

  // Extract unique categories from all sessions and format for ExpoFilters
  const categories = useMemo(() => {
    const categoryMap = new Map<string, CategoryWithCount>();

    scheduleData.forEach(day => {
      day.sessions.forEach(session => {
        if (session.categories) {
          session.categories.forEach(cat => {
            if (!categoryMap.has(cat.id)) {
              // Count sessions for each category
              const sessionCount = scheduleData.reduce((count, d) => {
                return count + d.sessions.filter(s =>
                  s.categories?.some(c => c.id === cat.id)
                ).length;
              }, 0);

              categoryMap.set(cat.id, {
                id: cat.id,
                name: cat.name,
                slug: cat.slug,
                position: cat.position || 0,
                displayProperties: cat.displayProperties,
                exhibitorCount: sessionCount, // Using session count instead
                parent: cat.parent,
                type: 'category'
              });
            }
          });
        }
      });
    });

    return Array.from(categoryMap.values()).sort((a, b) => a.position - b.position);
  }, [scheduleData]);

  // Loading state
  if (isLoading) {
    return (
      <div className={`min-h-[calc(100vh-64px)]`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <ScheduleLoadingState />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className={`min-h-[calc(100vh-64px)]`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2">Unable to Load Schedule</h2>
            <p className="text-gray-600">There was an error loading the event schedule. Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  // No agenda state
  if (!agendas || agendas.length === 0) {
    return (
      <div className={`min-h-[calc(100vh-64px)]`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-gray-400 mb-4">
              <Calendar className="w-16 h-16 mx-auto" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Schedule Coming Soon</h2>
            <p className="text-gray-600">The event schedule is not available yet. Please check back later.</p>
          </div>
        </div>
      </div>
    );
  }

  // No days/sessions state
  if (scheduleData.length === 0) {
    return (
      <div className={`min-h-[calc(100vh-64px)]`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 text-center">
            <div className="text-gray-400 mb-4">
              <Calendar className="w-16 h-16 mx-auto" />
            </div>
            <h2 className="text-xl font-semibold mb-2">No Sessions Scheduled</h2>
            <p className="text-gray-600">There are no sessions scheduled for this event yet.</p>
          </div>
        </div>
      </div>
    );
  }

  const currentDay = selectedDayId
    ? scheduleData.find(day => day.id === selectedDayId)
    : scheduleData.find(day => day.date === selectedDay) || scheduleData[0];

  // Group sessions by time slot
  const groupedSessions = currentDay.sessions
    .filter(session => {
      const matchesSearch = searchTerm === '' ||
        session.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.speakers.some(speaker =>
          speaker.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      const matchesCategory = selectedCategories.length === 0 ||
        (session.categories && session.categories.some(cat => selectedCategories.includes(cat.id)));
      return matchesSearch && matchesCategory;
    })
    .reduce((acc, session) => {
      const timeSlot = acc.find(slot => slot.time === session.time);
      if (timeSlot) {
        timeSlot.sessions.push(session);
      } else {
        acc.push({
          time: session.time,
          endTime: session.endTime,
          sessions: [session]
        });
      }
      return acc;
    }, [] as Array<{
      time: string;
      endTime: string;
      sessions: ScheduleSession[];
    }>);

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedCategories([]);
  };

  const getFileIcon = (attachment: Attachment) => {
    const type = attachment.humanizedType || attachment.fileContentType || '';
    const lowerType = type.toLowerCase();

    if (lowerType.includes('pdf')) {
      return <FileText className="w-4 h-4" />;
    } else if (lowerType.includes('image') || lowerType.includes('jpg') || lowerType.includes('png')) {
      return <ImageIcon className="w-4 h-4" />;
    } else if (lowerType.includes('excel') || lowerType.includes('spreadsheet') || lowerType.includes('csv')) {
      return <FileSpreadsheet className="w-4 h-4" />;
    } else if (lowerType.includes('word') || lowerType.includes('document')) {
      return <FileIcon className="w-4 h-4" />;
    } else {
      return <File className="w-4 h-4" />;
    }
  };

  const downloadAgendaFile = (attachment?: Attachment) => {
    // If specific attachment provided, download it
    if (attachment) {
      const downloadUrl = attachment.downloadUrl || attachment.url || attachment.fileUrl;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      }
      return;
    }

    // Otherwise, download the first attachment
    const attachments = agendaData?.showableAttachments || agendaData?.attachments;
    if (attachments && attachments.length > 0) {
      const firstAttachment = attachments[0];
      const downloadUrl = firstAttachment.url || firstAttachment.downloadUrl || firstAttachment.fileUrl;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      }
    }
  };

  const downloadAllAttachments = async (attachments: Attachment[]) => {
    // Download files sequentially with a small delay to avoid browser blocking
    for (let i = 0; i < attachments.length; i++) {
      const attachment = attachments[i];
      const downloadUrl = attachment.url || attachment.downloadUrl || attachment.fileUrl;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
        // Add a small delay between downloads to avoid browser popup blocking
        if (i < attachments.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    }
  };

  // Get agenda options
  const agendaOptions = agendaData?.agendaOption;

  return (
    <div className={`min-h-[calc(100vh-64px)]`}>
      {/* Main container with backdrop blur and semi-transparent background */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-32">
        <div className="bg-white backdrop-blur-sm shadow-xl rounded-xl border border-gray-200/50">
          <div className="p-6 sm:p-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">Schedule</h1>
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                  <Clock size={16} />
                  <span>All times shown in {getTimezoneLabel(eventTimeZone)}</span>
                </div>
              </div>
              {((agendaData?.attachments && agendaData.attachments.length > 0) || (agendaData?.showableAttachments && agendaData.showableAttachments.length > 0)) && (
                <div className="relative">
                  {(() => {
                    const attachments = agendaData?.showableAttachments || agendaData?.attachments || [];

                    // Single attachment - simple button
                    if (attachments.length === 1) {
                      const attachment = attachments[0];
                      return (
                        <button
                          onClick={() => downloadAgendaFile(attachment)}
                          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-all hover:shadow-lg"
                          style={{
                            backgroundColor: primaryColor,
                            filter: 'brightness(1)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(0.9)'}
                          onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(1)'}
                        >
                          {getFileIcon(attachment)}
                          <span className="hidden sm:inline">{attachment.name || 'Download Agenda'}</span>
                          <span className="sm:hidden">Download</span>
                        </button>
                      );
                    }

                    // Multiple attachments - dropdown
                    return (
                      <div>
                        <button
                          onClick={() => setIsDownloadMenuOpen(!isDownloadMenuOpen)}
                          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-all hover:shadow-lg"
                          style={{
                            backgroundColor: primaryColor,
                            filter: 'brightness(1)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(0.9)'}
                          onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(1)'}
                        >
                          <Download size={16} />
                          <span className="hidden sm:inline">Downloads</span>
                          <span className="sm:hidden">{attachments.length}</span>
                          <div className="flex items-center gap-1">
                            <div className="hidden sm:flex items-center justify-center w-5 h-5 bg-white/20 rounded-full text-xs">
                              {attachments.length}
                            </div>
                            <ChevronDown
                              size={16}
                              className={`transition-transform ${isDownloadMenuOpen ? 'rotate-180' : ''}`}
                            />
                          </div>
                        </button>

                        {/* Dropdown Menu */}
                        {isDownloadMenuOpen && (
                          <>
                            {/* Backdrop to close menu when clicking outside */}
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setIsDownloadMenuOpen(false)}
                            />

                            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-20">
                              <div className="p-2 border-b border-gray-100">
                                <p className="text-xs text-gray-500 font-medium px-2">
                                  {attachments.length} files available
                                </p>
                              </div>

                              <div className="max-h-80 overflow-y-auto">
                                {attachments.map((attachment, index) => (
                                  <button
                                    key={attachment.id || index}
                                    onClick={() => {
                                      downloadAgendaFile(attachment);
                                      setIsDownloadMenuOpen(false);
                                    }}
                                    className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors group"
                                  >
                                    <div
                                      className="p-2 rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors"
                                      style={{ color: primaryColor }}
                                    >
                                      {getFileIcon(attachment)}
                                    </div>
                                    <div className="flex-1 text-left">
                                      <p className="text-sm font-medium text-gray-900 line-clamp-1">
                                        {attachment.name || `Attachment ${index + 1}`}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {attachment.humanizedType || 'Document'}
                                        {attachment.fileSizeFormatted && ` • ${attachment.fileSizeFormatted}`}
                                      </p>
                                    </div>
                                    <Download
                                      size={14}
                                      className="text-gray-400 group-hover:text-gray-600 transition-colors"
                                    />
                                  </button>
                                ))}
                              </div>

                              {attachments.length > 5 && (
                                <div className="p-2 border-t border-gray-100 bg-gray-50">
                                  <button
                                    onClick={async () => {
                                      setIsDownloadMenuOpen(false);
                                      await downloadAllAttachments(attachments);
                                    }}
                                    className="w-full px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-2"
                                    style={{
                                      backgroundColor: primaryColor,
                                      color: 'white'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(0.9)'}
                                    onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(1)'}
                                  >
                                    <Download size={14} />
                                    Download All Files
                                  </button>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Agenda Description */}
            {agendaOptions?.agendaDescription && (
              <div className="prose prose-blue max-w-none">
                <div dangerouslySetInnerHTML={{ __html: agendaOptions.agendaDescription }} />
              </div>
            )}

            {/* Search and Filters */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <Search className="text-gray-400" size={20} />
                  </div>
                  <input
                    type="text"
                    placeholder="Search sessions, speakers, or topics..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 h-10 border rounded-lg focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors ${
                      selectedCategories.length > 0 ? '' : 'border-gray-300'
                    }`}
                    style={selectedCategories.length > 0 ? {
                      borderColor: primaryColor,
                      color: primaryColor
                    } : {}}
                  >
                    <Filter size={16} />
                    <span>Filter</span>
                    {selectedCategories.length > 0 && (
                      <span className="text-white px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: primaryColor }}>
                        {selectedCategories.length}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Filter Options */}
              {isFilterOpen && (
                <div className="border-t pt-4">
                  {categories.length > 0 ? (
                    <div>
                      <CategoryFilters
                        categories={categories}
                        selectedCategories={selectedCategories}
                        onChange={setSelectedCategories}
                        primaryColor={primaryColor}
                      />
                      {(selectedCategories.length > 0 || searchTerm) && (
                        <button
                          onClick={clearAllFilters}
                          className="mt-3 px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      <p className="text-sm">No categories available for filtering</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Day Tabs */}
            <DayTabs
              days={scheduleData}
              selectedDay={selectedDay}
              onDayChange={setSelectedDay}
              primaryColor={primaryColor}
              selectedDayId={selectedDayId}
              onDayChangeById={(dayId, date) => {
                setSelectedDayId(dayId);
                setSelectedDay(date);
              }}
            />

            {/* Sessions */}
            <div>
              {groupedSessions.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <Search size={48} className="mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
                  <p className="text-gray-600">
                    Try adjusting your search or filter criteria
                  </p>
                </div>
              ) : (
                <div className="space-y-8">
                  {groupedSessions.map((timeSlot, index) => (
                    <div key={`${timeSlot.time}-${index}`}>
                      {/* Time slot header */}
                      <div className="flex items-center gap-3 mb-4">
                        <Clock size={20} style={{ color: primaryColor }} />
                        <h3 className="text-lg font-semibold text-gray-900">
                          {timeSlot.time}
                        </h3>
                      </div>

                      {/* Sessions in this time slot */}
                      <div className="space-y-4">
                        {timeSlot.sessions.map((session) => (
                          <ExpandedSessionCard
                            key={session.id}
                            session={session}
                            onClick={() => {
                              setSelectedSession(session);
                              setShowMaterialsTab(false);
                            }}
                            onMaterialsClick={() => {
                              if (session.attachments && session.attachments.length > 0) {
                                setSelectedSession(session);
                                setShowMaterialsTab(true);
                              }
                            }}
                            agendaOptions={agendaOptions}
                            primaryColor={primaryColor}
                            eventDate={currentDay?.date}
                            eventTimeZone={eventTimeZone}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sponsors Section in separate card */}
        {agendaOptions?.showAllEventSponsorsSection && (
          <div className="mt-6">
            <div className="bg-white rounded-lg shadow-sm">
              <SponsorsSection
                sponsors={sponsorsResponse || []}
                isLoading={sponsorsLoading}
                onSponsorClick={setSelectedSponsor}
              />
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedSession && (
        <SessionModal
          session={selectedSession}
          onClose={() => {
            setSelectedSession(null);
            setShowMaterialsTab(false);
          }}
          timeZoneLabel={getTimezoneLabel(eventTimeZone)}
          onCalendarAdd={(type) => {
            const url = generateCalendarUrl(selectedSession, type as any, selectedDay, eventTimeZone);
            if (type === 'apple') {
              const link = document.createElement('a');
              link.href = url;
              link.download = `${selectedSession.title.replace(/\s+/g, '-')}.ics`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            } else {
              window.open(url, '_blank');
            }
          }}
          ModalWrapper={PortalModal}
          SpeakerModal={SpeakerModal}
          speakers={speakersData?.data?.speakers}
          primaryColor={primaryColor}
          eventDetails={eventDetails}
          initialTab={showMaterialsTab ? 'attachments' : 'details'}
        />
      )}

      {selectedSponsor && (
        <SponsorDetailModal
          sponsor={selectedSponsor}
          isOpen={!!selectedSponsor}
          onClose={() => setSelectedSponsor(null)}
        />
      )}
    </div>
  );
};